import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Mail, Phone, Trash2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adminApi, fmtDateTime } from "./adminApi";
import { euro } from "@/content/financing";

const QUOTE_STATUS = {
  nuova: "Da valutare",
  in_valutazione: "In valutazione",
  proposta_inviata: "Proposta inviata",
  chiusa: "Chiusa",
};

const FileButton = ({ q, f }) => {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    const win = window.open("", "_blank");
    setBusy(true);
    try {
      const { data } = await adminApi.get(f.url.replace(/^\/api/, ""), { responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (win) win.location.href = url; else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { win?.close(); toast.error("File non disponibile"); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={open} data-testid={`quote-open-${f.kind}-${q.id}`}
      className="inline-flex items-center gap-2 rounded-full bg-[#E6ECE4] px-4 py-2 text-sm text-[#1B3B28] hover:bg-[#D5DFD3] transition-colors">
      {busy ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
      {f.kind === "preventivo" ? "Preventivo" : "Panoramica"}
    </button>
  );
};

const QuoteCard = ({ q, onStatus, onDelete }) => (
  <div className="rounded-3xl bg-[#FCFBF7] border border-[#1B3B28]/10 p-5 sm:p-6" data-testid={`quote-item-${q.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p className="f-serif text-2xl text-[#1B3B28] leading-tight">{q.nome} {q.cognome}</p>
        <p className="text-xs text-[#526356] mt-1">{fmtDateTime(q.created_at)}</p>
      </div>
      <Select value={q.status} onValueChange={(v) => onStatus(q.id, v)}>
        <SelectTrigger className="w-[190px] rounded-full bg-[#F4F3EC] border-[#1B3B28]/15" data-testid={`quote-status-${q.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(QUOTE_STATUS).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`quote-status-option-${k}`}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    <p className="mt-4 text-[#1B3B28]" data-testid={`quote-plan-${q.id}`}>{euro(q.importo)} · {q.rate} rate da <b>{euro(q.rata)}</b></p>
    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#526356]">
      <a href={`tel:${q.telefono}`} className="inline-flex items-center gap-1.5 hover:text-[#1B3B28]"><Phone size={13} />{q.telefono}</a>
      <a href={`mailto:${q.email}`} className="inline-flex items-center gap-1.5 hover:text-[#1B3B28] break-all"><Mail size={13} />{q.email}</a>
    </div>
    {q.note && <p className="mt-3 text-sm text-[#526356] italic">"{q.note}"</p>}
    <div className="mt-5 flex flex-wrap items-center gap-2">
      {q.files.map((f) => <FileButton key={f.kind} q={q} f={f} />)}
      <button onClick={() => onDelete(q.id)} aria-label="Elimina richiesta" data-testid={`quote-delete-${q.id}`}
        className="ml-auto grid place-items-center w-9 h-9 rounded-full border border-[#1B3B28]/15 text-[#9A4B3C] hover:bg-[#F4E9E6]"><Trash2 size={14} /></button>
    </div>
  </div>
);

export const QuotesTab = () => {
  const [items, setItems] = useState(null);
  const load = () => adminApi.get("/admin/quotes").then(({ data }) => setItems(Array.isArray(data) ? data : [])).catch(() => { setItems([]); toast.error("Impossibile caricare i preventivi. Riprova tra poco."); });
  useEffect(() => { load(); }, []);
  const onStatus = async (id, status) => {
    await adminApi.patch(`/admin/quotes/${id}`, { status });
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
    toast.success("Stato aggiornato");
  };
  const onDelete = async (id) => {
    if (!window.confirm("Eliminare questa richiesta di preventivo?")) return;
    await adminApi.delete(`/admin/quotes/${id}`);
    setItems((xs) => xs.filter((x) => x.id !== id));
    toast.success("Richiesta eliminata");
  };
  if (items === null) return <Loader2 className="animate-spin text-[#3E6B48]" />;
  return (
    <div data-testid="quotes-tab">
      <p className="text-sm text-[#526356] max-w-2xl mb-6">Preventivi caricati dalla pagina "Dilaziona il tuo preventivo". Apri i file, valuta e aggiorna lo stato dopo aver ricontattato il paziente.</p>
      {items.length === 0
        ? <p className="text-[#526356]" data-testid="quotes-empty">Nessun preventivo ricevuto per ora.</p>
        : <div className="grid gap-4 lg:grid-cols-2">{items.map((q) => <QuoteCard key={q.id} q={q} onStatus={onStatus} onDelete={onDelete} />)}</div>}
    </div>
  );
};
